import { useEffect, useState } from 'react';

import { Popover, useMediaQuery, useTheme } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';

import {
  CustomActionBar,
  type CustomActionBarProps,
  CustomToolBar,
  dateRangePickerPaperSx,
  getStaticDateRangePickerSx,
} from '@modules/_shared/components/filters/quick-filters/DateFilter';
import { buildLastNDaysShortcuts } from '@modules/_shared/shared/utils/dateRangeShortcuts';
import { useLocale } from '@shared/hooks/locale';

type VisibilityDateRangePopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  startDateObj: Date;
  endDateObj: Date;
  minDate?: Date;
  setRange: (from: Date, to: Date) => void;
};

export const VisibilityDateRangePopover = ({
  anchorEl,
  onClose,
  startDateObj,
  endDateObj,
  minDate,
  setRange,
}: VisibilityDateRangePopoverProps) => {
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const open = Boolean(anchorEl);

  const [range, setDraftRange] = useState<DateRange<Date>>([
    startDateObj,
    endDateObj,
  ]);
  const [rangePosition, setRangePosition] = useState<'start' | 'end'>('start');

  useEffect(() => {
    if (open) {
      setDraftRange([startDateObj, endDateObj]);
      setRangePosition('start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAccept = (acceptedRange: DateRange<Date>) => {
    const [from, to] = acceptedRange;
    if (from && to) {
      setRange(from, to);
    }
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: [dateRangePickerPaperSx, { mt: 0.5 }] } }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateLocale}
        localeText={{ cancelButtonLabel: t('Cancel') }}
      >
        <StaticDateRangePicker
          calendars={isMobile ? 1 : 2}
          rangePosition={rangePosition}
          onRangePositionChange={setRangePosition}
          value={range}
          minDate={minDate}
          maxDate={new Date()}
          slots={{ actionBar: CustomActionBar, toolbar: CustomToolBar }}
          slotProps={{
            shortcuts: {
              items: buildLastNDaysShortcuts(t),
              changeImportance: 'set',
            },
            actionBar: {
              onCancel: onClose,
            } as CustomActionBarProps,
          }}
          onAccept={handleAccept}
          onChange={setDraftRange}
          sx={getStaticDateRangePickerSx(isTablet, isMobile)}
        />
      </LocalizationProvider>
    </Popover>
  );
};
